// src/pages/AttendanceOverview/components/LeaveToEmployeeModal.js
import React, { useEffect, useState } from "react";
import {
  Modal,
  DatePicker,
  AutoComplete,
  Input,
  Button,
  Row,
  Col,
  Typography,
  App,
} from "antd";
import dayjs from "dayjs";
import "./leaveModal.css"; // reuse same CSS as LeaveModal

const { Text } = Typography;

const LeaveToEmployeeModal = ({
  visible,
  onClose,
  onSubmit,
  weekEndDays = [],
  allUsers = [],
  isDisabled,
  isIndia = false,
}) => {
  const { modal } = App.useApp();

  const [leaveType, setLeaveType] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [notes, setNotes] = useState("");

  // Reset modal fields
  useEffect(() => {
    if (!visible) {
      setLeaveType(null);
      setEmployee(null);
      setFromDate(null);
      setToDate(null);
      setNotes("");
    }
  }, [visible]);

  // The selected employee's individual weekend override (if set) takes
  // precedence over the institution-wide weekEndDays prop — previously this
  // modal always used one static institution-wide value regardless of
  // which employee was picked.
  const selectedEmployeeRecord = allUsers.find((u) => u.id === employee?.id);
  const effectiveWeekendDays =
    selectedEmployeeRecord?.use_custom_weekend && selectedEmployeeRecord?.custom_weekend_days
      ? String(selectedEmployeeRecord.custom_weekend_days)
          .split(",")
          .map(Number)
          .filter((n) => !Number.isNaN(n))
      : Array.isArray(weekEndDays)
        ? weekEndDays
        : [];

  const disableDate = (current) => effectiveWeekendDays.includes(current.day());

  const leaveTypes = [
    {
      label: "Sick Leave",
      value: "sick_leave",
    },
    // Annual Leave and Casual Leave are the same pool — only the
    // country-appropriate label/value is offered, never both.
    isIndia
      ? { label: "Casual Leave", value: "casual_leave" }
      : { label: "Annual Leave", value: "annual_leave" },
    {
      label: "Short Sick Leave",
      value: "short_sick_leave",
    },
    {
      label: "Short Casual Leave",
      value: "short_casual_leave",
    },
    {
      label: "Flexi Leave",
      value: "flexi_leave",
    },
    {
      label: "Emergency Leave (Unpaid)",
      value: "emergency_leave",
    },
  ];

  const handleSave = () => {
    if (!leaveType)
      return modal.warning({ title: "Select Leave Type", centered: true });
    if (!employee)
      return modal.warning({ title: "Select Employee", centered: true });
    if (!fromDate)
      return modal.warning({ title: "Select From Date", centered: true });
    if (!toDate)
      return modal.warning({ title: "Select To Date", centered: true });
    if (!notes?.trim())
      return modal.warning({ title: "Notes is required", centered: true });

    const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
    const formattedToDate = dayjs(toDate).format("YYYY-MM-DD");

    if (formattedFromDate > formattedToDate)
      return modal.warning({
        title: "The 'From' date cannot be later than the 'To' date.",
        centered: true,
      });

    onSubmit({
      leaveType,
      employee,
      from_date: formattedFromDate,
      to_date: formattedToDate,
      notes,
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="leave-modal"
    >
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">
            Apply Leave to Employee
          </Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dayjs().format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {/* Fields */}
      <Row gutter={16} style={{ marginTop: "2rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            Leave Type <span className="form-span">*</span>
          </label>
          <AutoComplete
            style={{ width: "100%" }}
            options={leaveTypes.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            value={leaveType?.label}
            onSelect={(value, option) =>
              setLeaveType({ label: option.label, value: option.value })
            }
            placeholder="Select Leave Type"
            data-testid="leave_type"
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Leave Applied To <span className="form-span">*</span>
          </label>
          <AutoComplete
            style={{ width: "100%" }}
            options={allUsers.map((user) => ({
              label: user.user_name,
              value: user.id,
            }))}
            value={employee?.user_name}
            onSelect={(value, option) =>
              setEmployee({ user_name: option.label, id: option.value })
            }
            placeholder="Select Employee"
            data-testid="leave_applied_to"
          />
        </Col>
      </Row>

      {/* Dates */}
      <Row gutter={16} style={{ marginTop: "1.5rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            From Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={fromDate}
            onChange={(val) => setFromDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
            data-testid="leave_from_date"
          />
        </Col>
        <Col xs={24} sm={12}>
          <label className="form-label">
            To Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            value={toDate}
            onChange={(val) => setToDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
            data-testid="leave_to_date"
          />
        </Col>
      </Row>

      {/* Notes */}
      <div style={{ marginTop: "1.5rem" }}>
        <label className="form-label">
          Notes <span className="form-span">*</span>
        </label>
        <Input
          placeholder="Notes"
          data-testid="leave_applied_notes"
          value={notes}
          onChange={(e) => {
            const val = e.target.value;
            if (val.length <= 50) setNotes(val);
          }}
          onKeyDown={(e) => {
            if (
              notes &&
              notes.toString().length >= 50 &&
              !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                e.key
              )
            ) {
              e.preventDefault();
            }
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {notes.length}/50 characters
        </Text>
      </div>

      {/* Buttons */}
      <div className="attendance-buttons">
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          disabled={
            isDisabled ||
            !leaveType ||
            !employee ||
            !notes ||
            !fromDate ||
            !toDate
          }
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default LeaveToEmployeeModal;
